import { useState, useCallback, useEffect } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import Table from "../../components/Table/Table";
import {
    useGetAuditLogsByTypeQuery,
    useLazyGetAuditLogsByTypeQuery
} from "../../store/services/tenancy.service";
import { formatDateTime } from "../../utils/formatDateTime";
import {
    Box,
    Button,
    CircularProgress,
    Typography,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    IconButton,
    Table as MuiTable,
    TableBody,
    TableRow,
    TableCell
} from "@mui/material";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import CloseIcon from "@mui/icons-material/Close";
import { useTheme } from "@mui/material/styles";

AdminLogsTable.propTypes = {
    tab: PropTypes.string.isRequired
};

const getHeadCellsByType = (onViewDetails) => ({
    UserCreation: [
        { id: "dateTime", label: "Date Time", render: (row) => formatDateTime(row.dateTime), initialVisible: true },
        { id: "groupName", label: "Group name", initialVisible: true },
        { id: "usersCreated", label: "Users created", render: (row) => `${row.usersCreated} out of ${row.usersToCreate}`, initialVisible: true },
        { id: "status", label: "Status", initialVisible: true }
    ],
    SendReminder: [
        { id: "dateTime", label: "Date Time", render: (row) => formatDateTime(row.dateTime), initialVisible: true },
        {
            id: "viewDetails",
            label: "View Details",
            render: (row) => (
                <Button
                    variant="outlined"
                    size="small"
                    onClick={(e) => {
                        e.stopPropagation();
                        onViewDetails(row);
                    }}
                >
                    View
                </Button>
            ),
            initialVisible: true
        },
        { id: "uniqueUsersCount", label: "Emails to send", render: (row) => row.uniqueUsersCount ? parseInt(row.uniqueUsersCount) : '-', initialVisible: true },
        { id: "sentEmailCount", label: "Emails sent", render: (row) => row.sentEmailCount ? parseInt(row.sentEmailCount) : '-', initialVisible: true },
        { id: "status", label: "Status", initialVisible: true }
    ],
    UserManagement: [
        { id: "dateTime", label: "Date Time", render: (row) => formatDateTime(row.dateTime), initialVisible: true },
        { id: "message", label: "Email address", dataKey: "message", initialVisible: true },
        { id: "status", label: "Status", initialVisible: true },
        { id: "requestorEmail", label: "Requested by", initialVisible: true },
    ]
});

function ReminderDetailsModal({ open, onClose, row }) {
    const theme = useTheme();

    if (!row) return null;

    const isCourseSpecific = row.reminderSource === 'Course-Specific';
    const isBulk = row.reminderSource === 'Bulk';
    const isScheduled = row.reminderSource === 'Scheduled';
    const isLineManagerEscalation = row.reminderSource === 'Line Manager Escalation';

    // Parse skippedEmails - handle both JSON string and array formats
    const parseSkippedEmails = (data) => {
        if (!data) return [];
        if (Array.isArray(data)) return data;
        if (typeof data === 'string') {
            try {
                const parsed = JSON.parse(data);
                return Array.isArray(parsed) ? parsed : [];
            } catch {
                return [];
            }
        }
        return [];
    };

    const skippedEmailsList = parseSkippedEmails(row.skippedEmails);

    const detailRows = [
        {
            label: (
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    Source
                    <Tooltip title="Bulk: Manual send to all users with outstanding courses. Scheduled: Automatic reminders as defined in settings (eg. every 7 days after receiving a course). Course-Specific: Reminder for a single user about a specific course. Line Manager Escalation: Reminders sent to line managers about their direct reports' outstanding courses.">
                        <HelpOutlineIcon sx={{ fontSize: 16, color: "grey.500", cursor: "pointer" }} />
                    </Tooltip>
                </Box>
            ),
            value: row.reminderSource || '-'
        },
        (isBulk || isCourseSpecific) && {
            label: "Sent By",
            value: row.requestorEmail || '-'
        },
        isBulk && {
            label: "Total Outstanding Courses",
            value: row.totalOutstandingCourses ?? '-'
        },
        isCourseSpecific && {
            label: "Sent To",
            value: row.sentTo || '-'
        },
        isScheduled && {
            label: (
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    Days After
                    <Tooltip title="The number of days after a course is assigned before the first reminder is sent. Example: If set to 7, users receive their first reminder 7 days after the course was assigned (if not completed).">
                        <HelpOutlineIcon sx={{ fontSize: 16, color: "grey.500", cursor: "pointer" }} />
                    </Tooltip>
                </Box>
            ),
            value: row.daysToRemindAfter ? parseInt(row.daysToRemindAfter) : '-'
        },
        isScheduled && {
            label: (
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    Frequency
                    <Tooltip title="The number of days between subsequent reminders after the first one. Example: If set to 14, users get another reminder every 14 days until they complete the course.">
                        <HelpOutlineIcon sx={{ fontSize: 16, color: "grey.500", cursor: "pointer" }} />
                    </Tooltip>
                </Box>
            ),
            value: row.reminderFrequency ? parseInt(row.reminderFrequency) : '-'
        },
        isLineManagerEscalation && {
            label: (
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    Escalation Count
                    <Tooltip title="The total number of users escalated to their line managers in this batch.">
                        <HelpOutlineIcon sx={{ fontSize: 16, color: "grey.500", cursor: "pointer" }} />
                    </Tooltip>
                </Box>
            ),
            value: row.escalationCount ?? '-'
        },
        (isBulk || isScheduled) && skippedEmailsList.length > 0 && {
            label: (
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    Skipped Emails
                    <Tooltip title="Users who were skipped during this reminder batch. Reasons include: duplicate (already sent in this batch), no outstanding courses, or email send failed.">
                        <HelpOutlineIcon sx={{ fontSize: 16, color: "grey.500", cursor: "pointer" }} />
                    </Tooltip>
                </Box>
            ),
            value: skippedEmailsList.length
        }
    ].filter(Boolean);

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth={false}
            fullWidth
            PaperProps={{ sx: { borderRadius: "8px", backgroundColor: theme.palette.background.default, maxWidth: "660px" } }}
        >
            <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                Reminder Details
                <IconButton onClick={onClose} size="small">
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent>
                <MuiTable>
                    <TableBody>
                        {detailRows.map((detail, index) => (
                            <TableRow key={index}>
                                <TableCell sx={{ fontWeight: 600, width: "40%" }}>
                                    {detail.label}
                                </TableCell>
                                <TableCell>
                                    {detail.value}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </MuiTable>

                {isLineManagerEscalation && row.escalations && row.escalations.length > 0 && (
                    <Box sx={{ mt: 3 }}>
                        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                            Escalation Details
                        </Typography>
                        <MuiTable size="small">
                            <TableBody>
                                <TableRow sx={{ backgroundColor: theme.palette.action.hover }}>
                                    <TableCell sx={{ fontWeight: 600 }}>User Email</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Line Manager Email</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                            Reminder Periods Outstanding
                                            <Tooltip title="How many reminder cycles the user's oldest incomplete course has reached, counting from when it was assigned.">
                                                <HelpOutlineIcon sx={{ fontSize: 16, color: "grey.500", cursor: "pointer" }} />
                                            </Tooltip>
                                        </Box>
                                    </TableCell>
                                </TableRow>
                                {row.escalations.map((escalation, index) => (
                                    <TableRow key={index}>
                                        <TableCell>{escalation.userEmail || '-'}</TableCell>
                                        <TableCell>{escalation.lineManagerEmail || '-'}</TableCell>
                                        <TableCell>{escalation.reminderNumber ?? '-'}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </MuiTable>
                    </Box>
                )}

                {(isBulk || isScheduled) && skippedEmailsList.length > 0 && (
                    <Box sx={{ mt: 3 }}>
                        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                            Skipped Email Details
                        </Typography>
                        <MuiTable size="small">
                            <TableBody>
                                <TableRow sx={{ backgroundColor: theme.palette.action.hover }}>
                                    <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Reason</TableCell>
                                </TableRow>
                                {skippedEmailsList.map((skipped, index) => (
                                    <TableRow key={index}>
                                        <TableCell>{skipped.Email || skipped.email || '-'}</TableCell>
                                        <TableCell>{skipped.Reason || skipped.reason || '-'}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </MuiTable>
                    </Box>
                )}
            </DialogContent>
        </Dialog>
    );
}

ReminderDetailsModal.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    row: PropTypes.object
};

export default function AdminLogsTable({ tab }) {
    const { tenancyId } = useSelector((state) => state.tenancy);
    const [selected, setSelected] = useState([]);
    const [pageSize] = useState(100);
    const [detailsModalOpen, setDetailsModalOpen] = useState(false);
    const [selectedRow, setSelectedRow] = useState(null);

    const [paginationModel, setPaginationModel] = useState({
        pageSize: pageSize,
        page: 0
    });

    const handleViewDetails = useCallback((row) => {
        setSelectedRow(row);
        setDetailsModalOpen(true);
    }, []);

    const handleCloseDetails = useCallback(() => {
        setDetailsModalOpen(false);
        setSelectedRow(null);
    }, []);

    // Fetch paginated data for the current tab type
    const {
        data: auditData,
        isLoading: initialLoading,
        error: auditLogsError,
        isFetching
    } = useGetAuditLogsByTypeQuery({
        tenancyId,
        auditType: tab,
        limit: pageSize
    });

    // Lazy query for loading more
    const [loadMore, { isLoading: loadingMore }] = useLazyGetAuditLogsByTypeQuery();

    const handleLoadMore = useCallback(async () => {
        if (auditData?.nextToken) {
            await loadMore({
                tenancyId,
                auditType: tab,
                limit: pageSize,
                nextToken: auditData.nextToken
            });
        }
    }, [auditData?.nextToken, loadMore, tenancyId, tab, pageSize]);

    const headCellsByType = getHeadCellsByType(handleViewDetails);
    const headCells = headCellsByType[tab] || [];
    const logs = auditData?.items || [];
    const isLoading = initialLoading || (isFetching && !logs.length);

    useEffect(() => {
        setPaginationModel(prev => ({ ...prev, page: 0 }));
    }, [tab]);

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <Table
                name="Audit Logs"
                data={logs}
                error={auditLogsError}
                loading={isLoading}
                headCells={headCells}
                search={true}
                editColumn={false}
                checkbox={false}
                toolbarActions={[]}
                showToolbarActions={false}
                onDelete={() => console.log("Delete Audit Log Not Allowed")}
                onCellClick={() => console.log("Cell Click Not Allowed")}
                selected={selected}
                setSelected={setSelected}
                maxTableHeight="calc(100vh - 335px)"
                initialVisibleColumns={headCells.filter(cell => cell.initialVisible).map(cell => cell.label)}
                paginationModel={paginationModel}
                setPaginationModel={setPaginationModel}
            />

            {/* Load More Section */}
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: 2,
                    py: 2,
                    borderTop: '1px solid',
                    borderColor: 'divider'
                }}
            >
                {auditData?.hasMore && (
                    <Button
                        variant="outlined"
                        onClick={handleLoadMore}
                        disabled={loadingMore || isFetching}
                        startIcon={loadingMore ? <CircularProgress size={16} /> : null}
                    >
                        {loadingMore ? 'Loading...' : 'Load More'}
                    </Button>
                )}

                <Typography variant="body2" color="text.secondary">
                    Showing {logs.length} records
                    {auditData?.hasMore && ' (more available)'}
                </Typography>
            </Box>

            {/* Reminder Details Modal */}
            <ReminderDetailsModal
                open={detailsModalOpen}
                onClose={handleCloseDetails}
                row={selectedRow}
            />
        </Box>
    );
}
